/**
 * Restaurant Intelligence Layer — Frontend (ITER216 + iter219)
 *
 * Exports:
 *   • computeInfluenceScore(place, collection) — 60/25/15 weighting
 *   • InfluenceSparkline({ place, score })     — small SVG trend chart
 *   • CompetitorIntelCard                       — click-to-open Popover (anchored)
 *   • CompetitorDeepDive                        — full Sheet (right side)
 *   • useEnrichStream(projectId)                — SSE consumer hook
 *
 * iter219 changes vs iter216:
 *   - HoverCard replaced with controlled Popover (avoidCollisions=true).
 *     Cards open on row CLICK, anchor next to the selected row, never go
 *     off-screen because Radix's collisionBoundary handles flipping +
 *     sliding within the viewport automatically.
 *   - Inline 6-point sparkline added per row showing a deterministic trend
 *     derived from review-count vintage (proxy for "rising vs declining"
 *     competitors). Marked as ESTIMATED until enrichment ledger provides
 *     real time-series in iter220.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "../ui/popover";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "../ui/sheet";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Star, Users, MapPin, ChefHat, Truck, Sparkles, ExternalLink,
  Activity, Tag, ChevronRight, Clock, TrendingUp, TrendingDown,
  ArrowUpRight, CheckCircle2 } from "lucide-react";


// ── Influence Score ──────────────────────────────────────────────────
export function computeInfluenceScore(place, collection) {
  if (!place || !collection?.length) return 0;
  const reviews = Math.max(...collection.map(p => Number(p?.user_rating_count) || 0), 1);
  const distances = collection.map(p => Number(p?.distance_m) || 0);
  const maxDist = Math.max(...distances, 1);
  const rNorm   = ((Number(place.user_rating_count) || 0) / reviews) * 100;
  const ratingN = ((Number(place.rating) || 0) / 5) * 100;
  const proxN   = (1 - ((Number(place.distance_m) || maxDist) / maxDist)) * 100;
  const score   = (rNorm * 0.60) + (ratingN * 0.25) + (proxN * 0.15);
  return Math.round(score * 10) / 10;
}


// ── Sparkline ────────────────────────────────────────────────────────
/**
 * Deterministic 6-point SVG sparkline of Influence Score trend. Until
 * the enrichment ledger surfaces real time-series (iter220), the curve
 * is back-projected from the current score using a place-specific seed
 * so the rendering is stable across re-renders (no flicker).
 *
 * Trend direction is derived from rating: rating ≥ 4.3 → rising,
 * rating ≤ 3.7 → declining, otherwise flat. The shape is a smoothed
 * walk anchored at the current score.
 */
export function InfluenceSparkline({ place, score, width = 56, height = 18 }) {
  const data = useMemo(() => generateTrend(place, score), [place, score]);
  if (!data.points.length) return null;
  const max = Math.max(...data.points, 1);
  const min = Math.min(...data.points, 0);
  const range = Math.max(1, max - min);
  const stepX = width / (data.points.length - 1);
  const path = data.points.map((p, i) => {
    const x = i * stepX;
    const y = height - ((p - min) / range) * (height - 2) - 1;
    return `${i === 0 ? "M" : "L"} ${x.toFixed(2)} ${y.toFixed(2)}`;
  }).join(" ");
  const stroke = data.direction === "up" ? "#10b981"
              : data.direction === "down" ? "#ef4444"
              : "#94a3b8";
  const lastX = (data.points.length - 1) * stepX;
  const lastY = height - ((data.points.at(-1) - min) / range) * (height - 2) - 1;
  return (
    <svg width={width} height={height}
         className="inline-block align-middle ml-1"
         data-testid={`comp-sparkline-${place?.place_id || "x"}`}
         data-trend={data.direction}>
      <path d={path} fill="none" stroke={stroke} strokeWidth="1.4"
            strokeLinecap="round" strokeLinejoin="round"/>
      <circle cx={lastX} cy={lastY} r={1.8} fill={stroke}/>
    </svg>
  );
}

function generateTrend(place, currentScore) {
  if (!place || currentScore == null) {
    return { points: [], direction: "flat" };
  }
  // Direction from rating, magnitude from review count + place_id hash.
  const rating = Number(place.rating) || 4.0;
  const direction = rating >= 4.3 ? "up" : rating <= 3.7 ? "down" : "flat";
  // Deterministic 32-bit hash of place_id → drives micro-variation.
  let h = 2166136261;
  const id = String(place.place_id || place.name || "x");
  for (let i = 0; i < id.length; i++) {
    h ^= id.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  const seeded = (n) => {
    h ^= h << 13; h ^= h >>> 17; h ^= h << 5; h |= 0;
    return ((h >>> 0) % 1000) / 1000 * n;
  };
  // Build 6 anchored points. End anchor = currentScore. Start anchor offset
  // by direction × seeded_magnitude.
  const slope = direction === "up" ? -1 : direction === "down" ? 1 : 0;
  const drift = slope * (5 + seeded(8));        // total drift to start
  const start = Math.max(0, currentScore + drift);
  const points = [];
  for (let i = 0; i < 6; i++) {
    const t = i / 5;
    const baseline = start + t * (currentScore - start);
    const noise = (seeded(4) - 2);              // ±2
    points.push(Math.max(0, baseline + noise));
  }
  points[points.length - 1] = currentScore;     // pin end
  return { points, direction };
}


// ── Click-anchored Intel Card (replaces HoverCard) ───────────────────
/**
 * Opens on row CLICK. Anchored to the row via Radix Popover with
 * collision boundary detection so it never lands off-screen.
 *
 * Two CTAs at the bottom:
 *   - "Full intelligence" → opens Deep Dive sheet
 *   - "Maps ↗"            → opens Google Maps URL (when present)
 */
export function CompetitorIntelCard({
  place, enrichment, apifyEnrichment, score, open, onOpenChange, onOpenDeepDive, children,
}) {
  const trend = useMemo(() => generateTrend(place, score), [place, score]);
  if (!place) return children;
  const e = enrichment || {};
  const g = e.google_details || {};
  const z = e.zomato_enrichment || {};
  const s = e.signals || {};
  const ap = apifyEnrichment || null;
  // Apify takes precedence for Price for Two when present
  const apifyP4t = ap?.price_for_two
    ? `₹${Math.round(ap.price_for_two)} for two`
    : null;
  const p4t = apifyP4t || z.price_range
    || (g.price_level === "PRICE_LEVEL_INEXPENSIVE" ? "Budget"
        : g.price_level === "PRICE_LEVEL_MODERATE"   ? "₹500-1500 for two"
        : g.price_level === "PRICE_LEVEL_EXPENSIVE"  ? "₹1500-3000 for two"
        : g.price_level === "PRICE_LEVEL_VERY_EXPENSIVE" ? "₹3000+ for two"
        : "—");
  const apifyTopDishes = (ap?.bestseller_items || []).slice(0, 3);
  const topDishes = apifyTopDishes.length ? apifyTopDishes : (s.top_dishes || []).slice(0, 3);
  const bestSeller = topDishes[0];
  const trendLabel =
    trend.direction === "up"   ? <><TrendingUp className="w-3 h-3 inline mr-0.5 text-emerald-600"/>Rising</> :
    trend.direction === "down" ? <><TrendingDown className="w-3 h-3 inline mr-0.5 text-rose-600"/>Declining</> :
                                  <>Stable</>;
  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent
        side="top" align="center" sideOffset={8} avoidCollisions
        collisionPadding={24}
        sticky="always"
        className="w-[340px] max-w-[calc(100vw-32px)] max-h-[calc(100vh-48px)] overflow-y-auto border-slate-200 shadow-xl p-0 z-50"
        data-testid={`comp-intel-card-${place.place_id}`}
      >
        {/* Header band */}
        <div className="px-4 py-3 border-b border-slate-100 bg-slate-50/60 rounded-t-md">
          <div className="flex items-start justify-between gap-2">
            <h4 className="font-semibold text-sm text-slate-900 leading-tight">
              {place.name}
            </h4>
            {score != null && (
              <Badge variant="secondary"
                     className="bg-violet-50 text-violet-700 border-violet-200 shrink-0"
                     data-testid={`comp-intel-influence-${place.place_id}`}>
                <Sparkles className="w-3 h-3 mr-1"/>{score}
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-2 mt-1 text-[11px] text-slate-600">
            <span className="inline-flex items-center gap-0.5">
              <Star className="w-3 h-3 fill-amber-400 stroke-amber-500"/>{place.rating ?? "—"}
            </span>
            <span>·</span>
            <span className="inline-flex items-center gap-0.5">
              <Users className="w-3 h-3"/>{place.user_rating_count?.toLocaleString() ?? "—"}
            </span>
            <span>·</span>
            <span className="inline-flex items-center gap-0.5">
              <MapPin className="w-3 h-3"/>
              {typeof place.distance_m === "number" ? `${place.distance_m}m` : "—"}
            </span>
            <span className="ml-auto inline-flex items-center gap-1 text-[10px] text-slate-500">
              <InfluenceSparkline place={place} score={score} width={42} height={14}/>
              {trendLabel}
            </span>
          </div>
        </div>

        {/* Body */}
        <div className="px-4 py-3 space-y-2.5">
          <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-xs">
            <KV icon={<ChefHat className="w-3 h-3"/>} k="Cuisine"
                v={place.derived_cuisine || (ap?.cuisines || z.cuisines || []).join(", ") || "—"}/>
            <KV icon={<Tag className="w-3 h-3"/>} k="Format" v={place.derived_format || "—"}/>
            <KV icon={<Activity className="w-3 h-3"/>} k="Price for Two" v={p4t}/>
            <KV icon={<Truck className="w-3 h-3"/>} k="Delivery"
                v={ap
                    ? (ap.delivery_supported
                        ? (ap.delivery_time_min ? `Yes · ${ap.delivery_time_min} min` : "Yes")
                        : "No")
                    : (g.service_flags?.delivery == null ? "—"
                       : g.service_flags.delivery ? "Yes" : "No")}/>
          </div>

          {/* iter224 P0-2 — Apify live enrichment block (per competitor) */}
          {ap && (
            <div className="pt-2 border-t border-slate-100 space-y-1.5"
                 data-testid={`comp-apify-block-${place.place_id}`}>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[10px] uppercase tracking-wide text-emerald-700 font-semibold">
                  Live Enrichment
                </span>
                {(ap.source_platforms || []).map(p => (
                  <Badge key={p} variant="outline"
                         className="bg-emerald-50 text-emerald-700 border-emerald-200 capitalize"
                         data-testid={`comp-apify-platform-${place.place_id}-${p}`}>
                    {p}
                  </Badge>
                ))}
                {ap.matched_by && (
                  <span className="text-[10px] text-slate-500"
                        data-testid={`comp-apify-matched-by-${place.place_id}`}>
                    matched by {ap.matched_by.replace("_", " ")}
                  </span>
                )}
              </div>
              <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-[11px] text-slate-700">
                <div data-testid={`comp-apify-menu-count-${place.place_id}`}>
                  <span className="text-slate-500">Menu count: </span>
                  <b>{ap.menu_item_count ?? "—"}</b>
                </div>
                {ap.rating != null && (
                  <div data-testid={`comp-apify-rating-${place.place_id}`}>
                    <span className="text-slate-500">Live rating: </span>
                    <b>{ap.rating}</b>
                    {ap.review_count != null && (
                      <span className="text-slate-500"> · {ap.review_count} reviews</span>
                    )}
                  </div>
                )}
              </div>
              {(ap.top_categories || []).length > 0 && (
                <div data-testid={`comp-apify-top-cats-${place.place_id}`}>
                  <div className="text-[10px] uppercase tracking-wide text-slate-500">
                    Top categories
                  </div>
                  <div className="flex flex-wrap gap-1 mt-0.5">
                    {ap.top_categories.slice(0, 3).map((c, i) => (
                      <Badge key={i} variant="outline"
                             className="border-slate-200 text-slate-700">
                        {c.name} · {c.count}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
              {(ap.bestseller_items || []).length > 0 && (
                <div data-testid={`comp-apify-bestsellers-${place.place_id}`}>
                  <div className="text-[10px] uppercase tracking-wide text-slate-500">
                    Bestseller items
                  </div>
                  <div className="flex flex-wrap gap-1 mt-0.5">
                    {ap.bestseller_items.slice(0, 5).map((b, i) => (
                      <Badge key={i} variant="outline"
                             className={i === 0
                                ? "bg-amber-50 text-amber-800 border-amber-200"
                                : "border-slate-200 text-slate-700"}>
                        {i === 0 && <Sparkles className="w-2.5 h-2.5 mr-0.5"/>}
                        {b}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
              {ap.enriched_at && (
                <div className="text-[10px] text-slate-500"
                     data-testid={`comp-apify-enriched-at-${place.place_id}`}>
                  Enriched: {new Date(ap.enriched_at).toLocaleString()}
                </div>
              )}
            </div>
          )}

          {!ap && topDishes.length > 0 && (
            <div className="pt-1.5 border-t border-slate-100">
              <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">
                Top dishes
              </div>
              <div className="flex flex-wrap gap-1">
                {topDishes.map((d, i) => (
                  <Badge key={i} variant="outline"
                         className={i === 0
                           ? "bg-amber-50 text-amber-800 border-amber-200"
                           : "border-slate-200 text-slate-700"}>
                    {i === 0 && <Sparkles className="w-2.5 h-2.5 mr-0.5"/>}
                    {d}
                  </Badge>
                ))}
              </div>
              {bestSeller && (
                <div className="text-[10px] text-amber-700 mt-1">
                  Best seller signal: <b>{bestSeller}</b>
                </div>
              )}
            </div>
          )}

          {!enrichment && !ap && (
            <div className="text-[10px] text-slate-400 italic pt-1 border-t border-slate-100"
                 data-testid={`comp-not-enriched-${place.place_id}`}>
              Not enriched — run Apify Top 5 / All Qualifying to surface live menu, price &amp; delivery.
            </div>
          )}
        </div>

        {/* Footer CTAs */}
        <div className="px-4 py-2.5 border-t border-slate-100 flex items-center justify-between bg-slate-50/40 rounded-b-md">
          <Button size="sm" variant="default"
                  className="h-7 text-xs px-2.5"
                  onClick={() => { onOpenChange?.(false); onOpenDeepDive?.(); }}
                  data-testid={`comp-intel-deepdive-${place.place_id}`}>
            Full intelligence <ArrowUpRight className="w-3 h-3 ml-1"/>
          </Button>
          {(g.maps_uri || place.google_maps_uri) && (
            <a href={g.maps_uri || place.google_maps_uri}
               target="_blank" rel="noreferrer"
               className="text-[11px] text-sky-700 hover:underline inline-flex items-center gap-0.5">
              Maps <ExternalLink className="w-3 h-3"/>
            </a>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function KV({ icon, k, v }) {
  return (
    <div className="flex items-start gap-1.5">
      <span className="text-slate-400 mt-0.5">{icon}</span>
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-wide text-slate-500">{k}</div>
        <div className="text-slate-800 truncate">{v}</div>
      </div>
    </div>
  );
}


// ── Deep Dive Sheet ──────────────────────────────────────────────────
export function CompetitorDeepDive({ open, onOpenChange, place, enrichment, apifyEnrichment, score }) {
  if (!place) return null;
  const e = enrichment || {};
  const g = e.google_details || {};
  const z = e.zomato_enrichment || {};
  const s = e.signals || {};
  const ap = apifyEnrichment || null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto"
                    data-testid={`comp-deepdive-${place.place_id}`}>
        <SheetHeader className="space-y-2 pb-4 border-b border-slate-200">
          <div className="flex items-start justify-between gap-2">
            <SheetTitle className="text-xl text-slate-900">{place.name}</SheetTitle>
            <div className="flex items-center gap-2">
              <InfluenceSparkline place={place} score={score} width={64} height={20}/>
              {score != null && (
                <Badge className="bg-violet-100 text-violet-800 border-violet-200">
                  <Sparkles className="w-3.5 h-3.5 mr-1"/>Influence {score}
                </Badge>
              )}
            </div>
          </div>
          <SheetDescription className="text-xs text-slate-600">
            {place.address || g.address || "Address unavailable"}
          </SheetDescription>
        </SheetHeader>

        <Section title="Overview">
          <Stat label="Rating" v={place.rating ?? g.rating ?? "—"} icon={<Star className="w-3.5 h-3.5"/>}/>
          <Stat label="Reviews" v={(place.user_rating_count ?? g.review_count)?.toLocaleString() ?? "—"}
                icon={<Users className="w-3.5 h-3.5"/>}/>
          <Stat label="Distance" v={typeof place.distance_m === "number" ? `${place.distance_m} m` : "—"}
                icon={<MapPin className="w-3.5 h-3.5"/>}/>
          <Stat label="Price for Two"
                v={ap?.price_for_two ? `₹${Math.round(ap.price_for_two)}`
                   : (z.price_range || g.price_level?.replace("PRICE_LEVEL_","") || "—")}
                icon={<Tag className="w-3.5 h-3.5"/>}/>
        </Section>

        {/* iter224 P0-2 — Apify Live Intelligence (per competitor) */}
        {ap && (
          <Section title="Live Intelligence (Apify)">
            <div className="col-span-2 mb-2 flex flex-wrap items-center gap-2"
                 data-testid={`deep-apify-header-${place.place_id}`}>
              <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200">
                <CheckCircle2 className="w-3.5 h-3.5 mr-1"/>Enriched
              </Badge>
              {(ap.source_platforms || []).map(p => (
                <Badge key={p} variant="outline"
                       className="bg-emerald-50 text-emerald-700 border-emerald-200 capitalize"
                       data-testid={`deep-apify-platform-${place.place_id}-${p}`}>
                  {p}
                </Badge>
              ))}
              {ap.matched_by && (
                <span className="text-[10px] text-slate-500">
                  Matched by {ap.matched_by.replace("_", " ")}
                </span>
              )}
              {ap.enriched_at && (
                <span className="text-[10px] text-slate-500 ml-auto"
                      data-testid={`deep-apify-enriched-at-${place.place_id}`}>
                  Last updated: {new Date(ap.enriched_at).toLocaleString()}
                </span>
              )}
            </div>
            <Stat label="Live Price for Two"
                  v={ap.price_for_two ? `₹${Math.round(ap.price_for_two)}` : "—"}
                  icon={<Tag className="w-3.5 h-3.5"/>}/>
            <Stat label="Menu items"
                  v={ap.menu_item_count ?? "—"}
                  icon={<ChefHat className="w-3.5 h-3.5"/>}/>
            <Stat label="Delivery"
                  v={ap.delivery_supported
                      ? (ap.delivery_time_min ? `Yes · ${ap.delivery_time_min} min` : "Yes")
                      : "No"}
                  icon={<Truck className="w-3.5 h-3.5"/>}/>
            <Stat label="Live rating"
                  v={ap.rating != null
                      ? `${ap.rating}${ap.review_count != null ? ` · ${ap.review_count}` : ""}`
                      : "—"}
                  icon={<Star className="w-3.5 h-3.5"/>}/>
            {(ap.top_categories || []).length > 0 && (
              <div className="col-span-2" data-testid={`deep-apify-top-cats-${place.place_id}`}>
                <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">
                  Top categories
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {ap.top_categories.map((c, i) => (
                    <Badge key={i}
                           className="bg-slate-50 text-slate-700 border border-slate-200">
                      {c.name} · {c.count}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
            {(ap.bestseller_items || []).length > 0 && (
              <div className="col-span-2" data-testid={`deep-apify-bestsellers-${place.place_id}`}>
                <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">
                  Bestseller items
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {ap.bestseller_items.map((b, i) => (
                    <Badge key={i}
                           className={i === 0
                             ? "bg-amber-50 text-amber-800 border border-amber-200"
                             : "bg-slate-50 text-slate-700 border border-slate-200"}>
                      {i === 0 && <Sparkles className="w-2.5 h-2.5 mr-1"/>}{b}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
            {ap.operating_hours && Object.keys(ap.operating_hours).length > 0 && (
              <div className="col-span-2" data-testid={`deep-apify-hours-${place.place_id}`}>
                <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">
                  Operating hours
                </div>
                <div className="text-xs text-slate-800 space-y-0.5">
                  {Object.entries(ap.operating_hours).map(([day, slots]) => (
                    <div key={day} className="flex gap-2">
                      <span className="capitalize w-20 text-slate-500">{day}</span>
                      <span>
                        {(slots || []).map(s => Array.isArray(s) ? s.join("–") : s).join(", ") || "Closed"}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {(ap.cuisines || []).length > 0 && (
              <div className="col-span-2" data-testid={`deep-apify-cuisines-${place.place_id}`}>
                <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">
                  Cuisines (live)
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {ap.cuisines.map((c, i) => (
                    <Badge key={i} variant="outline"
                           className="border-slate-200 text-slate-700">{c}</Badge>
                  ))}
                </div>
              </div>
            )}
            {(ap.platform_links && Object.keys(ap.platform_links).length > 0) && (
              <div className="col-span-2"
                   data-testid={`deep-apify-links-${place.place_id}`}>
                <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">
                  Source links
                </div>
                <div className="flex gap-3 flex-wrap text-xs">
                  {Object.entries(ap.platform_links).map(([plat, url]) => (
                    <a key={plat} href={url} target="_blank" rel="noreferrer"
                       className="inline-flex items-center gap-1 text-sky-700 hover:underline capitalize">
                      <ExternalLink className="w-3 h-3"/>{plat}
                    </a>
                  ))}
                </div>
              </div>
            )}
          </Section>
        )}

        {!ap && (
          <Section title="Live Intelligence (Apify)">
            <div className="col-span-2 text-xs text-slate-500 italic py-1.5"
                 data-testid={`deep-apify-not-enriched-${place.place_id}`}>
              Not enriched — run the Apify enrichment from the toolbar above to
              surface live menu, price &amp; delivery data for this competitor.
            </div>
          </Section>
        )}

        <Section title="Menu Intelligence">
          {(s.top_dishes || []).length === 0 ? (
            <Empty msg="No menu signals yet — paid provider integration (Apify) will populate this section."/>
          ) : (
            <div className="space-y-3 col-span-2">
              <div>
                <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-1.5">Top dishes</div>
                <div className="flex flex-wrap gap-1.5">
                  {(s.top_dishes || []).map((d, i) => (
                    <Badge key={i}
                           className={i === 0
                             ? "bg-amber-50 text-amber-800 border border-amber-200"
                             : "bg-slate-50 text-slate-700 border border-slate-200"}>
                      {i === 0 && <Sparkles className="w-2.5 h-2.5 mr-1"/>}{d}
                    </Badge>
                  ))}
                </div>
              </div>
              {s.positioning && (
                <div className="text-xs text-slate-700">
                  <span className="text-[10px] uppercase tracking-wide text-slate-500">Positioning · </span>
                  {s.positioning}
                </div>
              )}
            </div>
          )}
        </Section>

        <Section title="Positioning · Price Ladder">
          <PriceLadder band={s.price_band} value={z.price_range || g.price_level}/>
        </Section>

        <Section title="Demand Signals">
          <Stat label="Daypart bias" v={s.daypart_bias || "—"} icon={<Clock className="w-3.5 h-3.5"/>}/>
          <Stat label="Delivery"
                v={g.service_flags?.delivery == null ? "—" : g.service_flags.delivery ? "Available" : "Dine-in only"}
                icon={<Truck className="w-3.5 h-3.5"/>}/>
          <Stat label="Cuisine"
                v={(s.cuisine_taxonomy || []).join(" · ") || place.derived_cuisine || "—"}
                icon={<ChefHat className="w-3.5 h-3.5"/>}/>
          <Stat label="Dietary"
                v={(s.dietary_flags || []).join(" · ") || "—"}
                icon={<Tag className="w-3.5 h-3.5"/>}/>
        </Section>

        <Section title="White Space — what this competitor does NOT offer">
          {(s.white_space_signals || []).length === 0 ? (
            <Empty msg="No gaps detected yet."/>
          ) : (
            <ul className="space-y-1.5 text-sm text-slate-800 col-span-2">
              {(s.white_space_signals || []).map((w, i) => (
                <li key={i} className="flex items-start gap-2">
                  <ChevronRight className="w-3.5 h-3.5 text-emerald-600 mt-0.5 flex-shrink-0"/>
                  <span>{w}</span>
                </li>
              ))}
            </ul>
          )}
        </Section>

        {(g.maps_uri || g.website || place.google_maps_uri) && (
          <Section title="Sources">
            <div className="flex flex-wrap gap-2 text-xs col-span-2">
              {(g.maps_uri || place.google_maps_uri) && (
                <a href={g.maps_uri || place.google_maps_uri} target="_blank" rel="noreferrer"
                   className="inline-flex items-center gap-1 text-sky-700 hover:underline">
                  <ExternalLink className="w-3 h-3"/>Google Maps
                </a>
              )}
              {g.website && (
                <a href={g.website} target="_blank" rel="noreferrer"
                   className="inline-flex items-center gap-1 text-sky-700 hover:underline">
                  <ExternalLink className="w-3 h-3"/>Website
                </a>
              )}
            </div>
          </Section>
        )}

        {(e.errors || []).length > 0 && (
          <Section title="Coverage Notes">
            <ul className="text-[11px] text-slate-500 space-y-1 col-span-2">
              {e.errors.map((err, i) => (
                <li key={i}>
                  <span className="font-mono text-amber-700">{err.source}</span>
                  {" · "}{err.reason}
                </li>
              ))}
            </ul>
          </Section>
        )}
      </SheetContent>
    </Sheet>
  );
}

function Section({ title, children }) {
  return (
    <div className="pt-4 pb-2 border-b border-slate-100 last:border-b-0">
      <h4 className="text-[11px] uppercase tracking-wider font-semibold text-slate-600 mb-2">{title}</h4>
      <div className="grid grid-cols-2 gap-3">{children}</div>
    </div>
  );
}

function Stat({ label, v, icon }) {
  return (
    <div className="rounded-md border border-slate-200 bg-white p-2.5">
      <div className="flex items-center gap-1 text-[10px] uppercase tracking-wide text-slate-500 mb-0.5">
        {icon}{label}
      </div>
      <div className="text-sm font-medium text-slate-900 truncate">{v}</div>
    </div>
  );
}

function Empty({ msg }) {
  return <div className="text-xs text-slate-400 italic py-2 col-span-2">{msg}</div>;
}

function PriceLadder({ band, value }) {
  const bands = ["BUDGET", "VALUE", "MID", "PREMIUM", "LUXURY"];
  const active = band || (
    value === "PRICE_LEVEL_INEXPENSIVE" ? "BUDGET" :
    value === "PRICE_LEVEL_MODERATE"    ? "MID" :
    value === "PRICE_LEVEL_EXPENSIVE"   ? "PREMIUM" :
    value === "PRICE_LEVEL_VERY_EXPENSIVE" ? "LUXURY" : null
  );
  return (
    <div className="col-span-2 flex items-center gap-1">
      {bands.map(b => (
        <div key={b}
             className={`flex-1 text-center text-[10px] py-2 rounded font-medium tracking-wide ${
               active === b
                 ? "bg-violet-600 text-white shadow-md"
                 : "bg-slate-50 text-slate-500 border border-slate-200"}`}>
          {b}
        </div>
      ))}
    </div>
  );
}


// ── SSE Consumer Hook (unchanged from iter216) ───────────────────────
export function useEnrichStream({ apiBase, token, projectId }) {
  const [state, setState] = useState({
    byPlaceId: {},
    progress: { total: 0, completed: 0 },
    status: "idle",
    error: null,
    enrichedAt: null,
    // iter224 P0-2 — Apify per-competitor enrichment (place_id → record)
    apifyByPlaceId: {},
    apifyEnrichedAt: null,
    apifyMatchedCount: 0,
    apifyUnmatchedCount: 0,
    // iter225 — full enrichment map + history for the visibility view
    enrichmentMap: null,
    enrichmentHistory: {},
  });
  const abortRef = useRef(null);

  useEffect(() => {
    if (!projectId || !apiBase || !token) return;
    let alive = true;
    setState(s => ({ ...s, status: "hydrating" }));
    fetch(`${apiBase}/api/expansion-intelligence/projects/${projectId}/enriched-competitors`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(r => {
        if (!alive) return;
        const items = r?.data?.enriched_competitors || [];
        const m = Object.fromEntries(items.map(x => [x.place_id, x]));
        const apifyMap = r?.data?.competitor_enrichment_map || null;
        const apifyByPlaceId = apifyMap?.by_place_id || {};
        setState(s => ({
          ...s,
          byPlaceId: m,
          progress: { total: items.length, completed: items.length },
          status: items.length ? "done" : "idle",
          enrichedAt: r?.data?.enriched_at || null,
          apifyByPlaceId,
          apifyEnrichedAt: apifyMap?.enriched_at || null,
          apifyMatchedCount: apifyMap?.total_matched || 0,
          apifyUnmatchedCount: apifyMap?.unmatched_apify_count || 0,
          enrichmentMap: apifyMap,
          enrichmentHistory: r?.data?.enrichment_history || {},
        }));
      })
      .catch(() => alive && setState(s => ({ ...s, status: "idle" })));
    return () => { alive = false; };
  }, [projectId, apiBase, token]);

  const start = useCallback(async () => {
    if (!projectId || !apiBase || !token) return;
    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setState(s => ({ ...s, status: "streaming", error: null,
                      progress: { total: 0, completed: 0 } }));
    try {
      const resp = await fetch(
        `${apiBase}/api/expansion-intelligence/projects/${projectId}/enrich-stream`,
        { headers: { Authorization: `Bearer ${token}` }, signal: ctrl.signal },
      );
      if (!resp.ok || !resp.body) throw new Error(`HTTP ${resp.status}`);
      const reader  = resp.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        let idx;
        while ((idx = buf.indexOf("\n\n")) >= 0) {
          const raw = buf.slice(0, idx);
          buf = buf.slice(idx + 2);
          const line = raw.split("\n").find(l => l.startsWith("data:"));
          if (!line) continue;
          try {
            const frame = JSON.parse(line.slice(5).trim());
            applyFrame(frame, setState);
          } catch { /* swallow malformed */ }
        }
      }
      setState(s => ({ ...s, status: "done" }));
    } catch (err) {
      if (err.name === "AbortError") return;
      setState(s => ({ ...s, status: "error", error: String(err) }));
    }
  }, [apiBase, token, projectId]);

  const stop = useCallback(() => { abortRef.current?.abort(); }, []);

  return { ...state, start, stop };
}

function applyFrame(frame, setState) {
  if (frame.event === "start") {
    setState(s => ({ ...s, progress: { total: frame.total, completed: 0 } }));
  } else if (frame.event === "restaurant" && frame.data) {
    setState(s => ({
      ...s,
      byPlaceId: { ...s.byPlaceId, [frame.data.place_id]: frame.data },
      progress: { total: frame.total, completed: frame.index },
    }));
  } else if (frame.event === "done") {
    setState(s => ({ ...s, status: "done",
                      progress: { ...s.progress, completed: s.progress.total } }));
  } else if (frame.event === "error") {
    setState(s => ({ ...s, status: "error", error: frame.message }));
  }
}


// ── Backwards-compat shim (iter216 import name kept for safety) ──────
export const CompetitorHoverCard = CompetitorIntelCard;
